/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { BUSINESS_RULES } from "@/lib/constants/business/rules";
import {
  deriveBudgetRates,
  type BudgetAssumptionsInput,
} from "../budgets/assumptions";
import type { OrganizationSettings } from "@/types/organization-settings";
import type { BudgetRates } from "./estimates-types";
import { logger } from "@/lib/utils/logger";

/**
 * Convert a rate to decimal form.
 *
 * **Canonical unit**: percent rates in the database and JSONB `assumptions`
 * blobs are stored as whole-number percentages (12 = 12%, 26 = 26%, 0.8 =
 * 0.8%). Divides by 100 unconditionally — no heuristic interpretation.
 *
 * History: this function previously treated values in `[0, 1)` as
 * already-decimal and values `>= 1` as whole-number percent. That heuristic
 * silently misread legitimately small percentages — Peabody Landscape's real
 * 2025 net margin of 0.8% was interpreted as 80% margin and produced
 * $187.74/hr labor pricing on a $29.80 wage. Audit (2026-04-24) confirmed
 * every persisted rate in both `budgets.assumptions` and
 * `organizations.settings` is already stored as whole-number percent, and no
 * caller passes a decimal literal. Dividing by 100 is therefore the correct
 * unambiguous behavior.
 *
 * - NaN or negative values → 0
 * - Everything else → val / 100
 */
export const toDecimalRate = (val: number, _fieldName?: string): number => {
  if (isNaN(val) || val < 0) return 0;
  return val / 100;
};

/**
 * Clamp a rate at a sane ceiling (e.g. 50% for margin/overhead) and log when
 * clamping kicks in. A rate above the ceiling is almost always a unit-entry
 * bug rather than a real business decision, so this both caps the damage and
 * leaves a trail to find and fix the source.
 */
function clampRateWithWarning(
  rate: number,
  max: number,
  fieldName: string,
): number {
  const safe = Math.max(rate, 0);
  if (safe > max) {
    logger.warn(`Pricing rate '${fieldName}' exceeds sane ceiling — clamping`, {
      fieldName,
      rawValue: rate,
      ceiling: max,
      clampedTo: max,
    });
    return max;
  }
  return safe;
}

/**
 * Clamp a raw dollar amount (not a percentage) at a ceiling and log when
 * clamping kicks in. Used for labor-hour absorption — the value is $/hr, not
 * a rate, so it doesn't share the percentage semantic.
 */
function clampDollarsWithWarning(
  value: number,
  max: number,
  fieldName: string,
): number {
  const safe = Math.max(value, 0);
  if (safe > max) {
    logger.warn(
      `Dollar amount '${fieldName}' exceeds sane ceiling — clamping`,
      { fieldName, rawValue: value, ceiling: max, clampedTo: max },
    );
    return max;
  }
  return safe;
}

/**
 * Extract budget rates from budget assumptions JSONB field
 * Converts percentage values to decimals and provides defaults
 */
export function extractBudgetRates(
  budgetAssumptions: Record<string, unknown> | null | undefined,
): BudgetRates | null {
  if (!budgetAssumptions || typeof budgetAssumptions !== "object") {
    return null;
  }

  // Phase H.4: Return null for effectively-empty assumptions so the caller
  // cascade can fall through to the next layer. Previously this function
  // returned a non-null shell for empty-ish input (with hardcoded defaults
  // for laborBurdenRate + basis fields), which was the root cause of the
  // 2026-04-15 modal bug — the shell blocked the org-settings fallback
  // inside fetchBudgetRatesForEstimate.
  //
  // Safe to land because: every runtime caller now goes through
  // resolveEstimateRates (or its resolveBudgetRatesStandalone variant),
  // which treats an "empty shell" as null via isEmptyBudgetShell. This
  // change makes that detection no longer necessary at the caller layer —
  // the source function just doesn't produce shells anymore.
  const hasAnyRateField =
    typeof budgetAssumptions.overhead_recovery_rate === "number" ||
    typeof budgetAssumptions.overhead_rate === "number" ||
    typeof budgetAssumptions.net_profit_margin === "number" ||
    typeof budgetAssumptions.netMarginPercent === "number" ||
    typeof budgetAssumptions.contingency_rate === "number" ||
    typeof budgetAssumptions.overhead_recovery_method === "string" ||
    typeof budgetAssumptions.overhead_fixed_amount === "number" ||
    typeof budgetAssumptions.overhead_per_labor_hour === "number" ||
    typeof budgetAssumptions.material_markup_rate === "number" ||
    typeof budgetAssumptions.subcontractor_markup_rate === "number" ||
    typeof budgetAssumptions.equipment_markup_rate === "number" ||
    (typeof budgetAssumptions.category_overhead_rates === "object" &&
      budgetAssumptions.category_overhead_rates !== null) ||
    typeof budgetAssumptions.labor_burden_rate === "number" ||
    typeof budgetAssumptions.laborBurdenPercent === "number" ||
    // Also surface assumptions blobs that only have allocation data — those
    // feed into cost distribution and are legitimately informative.
    typeof budgetAssumptions.labor_allocation === "number" ||
    typeof budgetAssumptions.material_allocation === "number";
  if (!hasAnyRateField) {
    return null;
  }

  // Extract overhead recovery method
  const overheadRecoveryMethod = budgetAssumptions.overhead_recovery_method as
    | "percentage_direct_costs"
    | "percentage_revenue"
    | "fixed_amount"
    | "activity_based"
    | undefined;

  // Extract overhead basis (direct costs or revenue)
  const overheadBasis =
    (budgetAssumptions.overhead_basis as "direct" | "revenue" | undefined) ??
    "direct";

  // Extract overhead rates - budget stores percentages, convert to decimals
  // Extract and validate overhead rate - must be < 1 to prevent division issues
  // Note: QuickBooks-imported "overhead" is an absolute dollar amount (total company overhead),
  // not a per-estimate rate. Do NOT derive overhead rate from absolute QB numbers — the ratio
  // (e.g. overhead/cogs) produces nonsensical rates. Use org settings or system default instead.
  // Return undefined when not configured — let cascade fall through to org settings
  const rawOverheadRate =
    typeof budgetAssumptions.overhead_recovery_rate === "number"
      ? toDecimalRate(
          budgetAssumptions.overhead_recovery_rate,
          "overhead_recovery_rate",
        )
      : typeof budgetAssumptions.overhead_rate === "number"
        ? toDecimalRate(budgetAssumptions.overhead_rate, "overhead_rate")
        : undefined;
  // Clamp overhead rate to a sane ceiling. Above 50% is almost always a
  // unit-entry bug (e.g. 0.8 silently read as 80%); above 99% blows up the
  // pricing formula. We clamp + log so the pipeline stays safe while the
  // suspicious value surfaces for investigation.
  const overheadRecoveryRate =
    rawOverheadRate !== undefined
      ? clampRateWithWarning(
          rawOverheadRate,
          BUSINESS_RULES.MAX_SANE_OVERHEAD_RATE,
          "overheadRate",
        )
      : undefined;

  // Extract activity-based overhead rates if method is activity_based
  // Format: { category_overhead_rates: { materials: 20, labor: 15, equipment: 25 } }
  const categoryOverheadRatesRaw = budgetAssumptions.category_overhead_rates as
    | {
        materials?: number;
        labor?: number;
        equipment?: number;
        subcontractors?: number;
      }
    | undefined;

  const categoryOverheadRates =
    overheadRecoveryMethod === "activity_based" && categoryOverheadRatesRaw
      ? {
          materials: categoryOverheadRatesRaw.materials
            ? clampRateWithWarning(
                toDecimalRate(
                  categoryOverheadRatesRaw.materials,
                  "category_overhead_rates.materials",
                ),
                BUSINESS_RULES.MAX_SANE_OVERHEAD_RATE,
                "categoryOverheadRates.materials",
              )
            : overheadRecoveryRate,
          labor: categoryOverheadRatesRaw.labor
            ? clampRateWithWarning(
                toDecimalRate(
                  categoryOverheadRatesRaw.labor,
                  "category_overhead_rates.labor",
                ),
                BUSINESS_RULES.MAX_SANE_OVERHEAD_RATE,
                "categoryOverheadRates.labor",
              )
            : overheadRecoveryRate,
          equipment: categoryOverheadRatesRaw.equipment
            ? clampRateWithWarning(
                toDecimalRate(
                  categoryOverheadRatesRaw.equipment,
                  "category_overhead_rates.equipment",
                ),
                BUSINESS_RULES.MAX_SANE_OVERHEAD_RATE,
                "categoryOverheadRates.equipment",
              )
            : overheadRecoveryRate,
          subcontractors: categoryOverheadRatesRaw.subcontractors
            ? clampRateWithWarning(
                toDecimalRate(
                  categoryOverheadRatesRaw.subcontractors,
                  "category_overhead_rates.subcontractors",
                ),
                BUSINESS_RULES.MAX_SANE_OVERHEAD_RATE,
                "categoryOverheadRates.subcontractors",
              )
            : overheadRecoveryRate,
        }
      : undefined;

  // Extract per-item profit margin from canonical field (net_profit_margin)
  // or legacy field (netMarginPercent). NEVER use profit_margin or
  // grossMarginPercent — those are company-level gross margin for reporting.
  const rawProfitMargin =
    typeof budgetAssumptions.net_profit_margin === "number"
      ? toDecimalRate(budgetAssumptions.net_profit_margin, "net_profit_margin")
      : typeof budgetAssumptions.netMarginPercent === "number"
        ? toDecimalRate(budgetAssumptions.netMarginPercent, "netMarginPercent")
        : undefined;
  // Clamp profit margin at the sane ceiling (50%). Above that is almost always
  // a unit-entry mistake (e.g. a value of 0.8 being silently read as 80%);
  // clamping + logging caps the pricing blowup and surfaces the bad input.
  // Negative margins are preserved (loss-leader / promo scenarios).
  const profitMargin =
    rawProfitMargin !== undefined
      ? rawProfitMargin < 0
        ? rawProfitMargin
        : clampRateWithWarning(
            rawProfitMargin,
            BUSINESS_RULES.MAX_SANE_PROFIT_MARGIN,
            "profitMargin",
          )
      : undefined;

  // marginRate follows profitMargin — return undefined when no per-item margin
  // is configured so the cascade can fall through to org settings.
  const marginRate = profitMargin;

  const contingencyRate =
    typeof budgetAssumptions.contingency_rate === "number"
      ? toDecimalRate(budgetAssumptions.contingency_rate)
      : undefined;
  const includeContingency = budgetAssumptions.include_contingency !== false;
  const contingencyBasis =
    (budgetAssumptions.contingency_basis as
      | "direct"
      | "subtotal"
      | undefined) ?? "direct";

  // Derive labor hourly rate and labor burden from allocation/assumptions
  const derived = deriveBudgetRates(
    budgetAssumptions as BudgetAssumptionsInput,
  );

  // When labor_budget_mode is "blended", use blended_labor_rate directly
  const laborBudgetMode = budgetAssumptions.labor_budget_mode as
    | string
    | undefined;
  const rawBlendedRate =
    typeof budgetAssumptions.blended_labor_rate === "number"
      ? budgetAssumptions.blended_labor_rate
      : undefined;
  const derivedLaborHourlyRate =
    laborBudgetMode === "blended" && rawBlendedRate !== undefined
      ? rawBlendedRate
      : (derived.laborHourlyRate ?? undefined);

  // Budget assumptions labor_burden_rate takes priority over org defaults
  // Bridge QuickBooks-style: laborBurdenPercent → labor_burden_rate
  const laborBurdenRate =
    derived.laborBurdenRate ??
    (typeof budgetAssumptions.labor_burden_rate === "number"
      ? toDecimalRate(budgetAssumptions.labor_burden_rate)
      : typeof budgetAssumptions.laborBurdenPercent === "number"
        ? toDecimalRate(budgetAssumptions.laborBurdenPercent as number)
        : BUSINESS_RULES.DEFAULT_LABOR_BURDEN_RATE);

  // Extract cost distribution from assumptions (synced from budget lines)
  const costDistributionRaw = budgetAssumptions.cost_distribution as
    | {
        materials?: number;
        labor?: number;
        equipment?: number;
        subcontractors?: number;
        other?: number;
      }
    | undefined;

  let costDistribution:
    | {
        materials?: number;
        labor?: number;
        equipment?: number;
        subcontractors?: number;
      }
    | undefined = costDistributionRaw
    ? {
        materials:
          typeof costDistributionRaw.materials === "number"
            ? costDistributionRaw.materials
            : undefined,
        labor:
          typeof costDistributionRaw.labor === "number"
            ? costDistributionRaw.labor
            : undefined,
        equipment:
          typeof costDistributionRaw.equipment === "number"
            ? costDistributionRaw.equipment
            : undefined,
        subcontractors:
          typeof costDistributionRaw.subcontractors === "number"
            ? costDistributionRaw.subcontractors
            : undefined,
      }
    : undefined;

  // Fallback: derive from budget allocation fields when cost_distribution not synced
  if (!costDistribution) {
    const mat =
      typeof budgetAssumptions.material_allocation === "number"
        ? budgetAssumptions.material_allocation
        : 0;
    const lab =
      typeof budgetAssumptions.labor_allocation === "number"
        ? budgetAssumptions.labor_allocation
        : 0;
    const eq =
      typeof budgetAssumptions.equipment_allocation === "number"
        ? budgetAssumptions.equipment_allocation
        : 0;
    const sub =
      typeof budgetAssumptions.subcontractor_allocation === "number"
        ? budgetAssumptions.subcontractor_allocation
        : 0;
    const total = mat + lab + eq + sub;
    if (total > 0) {
      costDistribution = {
        materials: mat / total,
        labor: lab / total,
        equipment: eq / total,
        subcontractors: sub / total,
      };
    }
  }

  // Labor hour absorption fields. Clamped at a sane ceiling — values above
  // MAX_SANE_OVERHEAD_PER_LABOR_HOUR are almost always mis-entered annual or
  // monthly overhead totals. The clamp protects pricing while the log lets us
  // trace the bad input back to its source form.
  const rawOverheadPerLaborHour =
    typeof budgetAssumptions.overhead_per_labor_hour === "number"
      ? budgetAssumptions.overhead_per_labor_hour
      : undefined;
  const overheadPerLaborHour =
    rawOverheadPerLaborHour !== undefined && rawOverheadPerLaborHour > 0
      ? clampDollarsWithWarning(
          rawOverheadPerLaborHour,
          BUSINESS_RULES.MAX_SANE_OVERHEAD_PER_LABOR_HOUR,
          "overheadPerLaborHour",
        )
      : rawOverheadPerLaborHour;
  const materialMarkupRate =
    typeof budgetAssumptions.material_markup_rate === "number"
      ? toDecimalRate(budgetAssumptions.material_markup_rate)
      : undefined;
  const subcontractorMarkupRate =
    typeof budgetAssumptions.subcontractor_markup_rate === "number"
      ? toDecimalRate(budgetAssumptions.subcontractor_markup_rate)
      : undefined;
  const equipmentMarkupRate =
    typeof budgetAssumptions.equipment_markup_rate === "number"
      ? toDecimalRate(budgetAssumptions.equipment_markup_rate)
      : undefined;

  return {
    overheadRate: overheadRecoveryRate,
    overheadRecoveryMethod,
    overheadBasis,
    overheadFixedAmount:
      typeof budgetAssumptions.overhead_fixed_amount === "number"
        ? budgetAssumptions.overhead_fixed_amount
        : undefined,
    overheadCap:
      typeof budgetAssumptions.overhead_cap === "number"
        ? budgetAssumptions.overhead_cap
        : undefined,
    overheadFloor:
      typeof budgetAssumptions.overhead_floor === "number"
        ? budgetAssumptions.overhead_floor
        : undefined,
    categoryOverheadRates,
    marginRate,
    profitMargin,
    contingencyRate,
    contingencyBasis,
    includeContingency,
    derivedLaborHourlyRate,
    laborBurdenRate,
    costDistribution,
    overheadPerLaborHour,
    materialMarkupRate,
    subcontractorMarkupRate,
    equipmentMarkupRate,
  };
}

/**
 * Convert organization settings to BudgetRates.
 * Used as fallback when no budget is assigned to an estimate.
 *
 * Settings store margins (% of selling price). The engine uses profitMargin
 * directly: Price = Base / (1 - margin). No margin conversion needed.
 */
export function orgSettingsToBudgetRates(
  settings: OrganizationSettings,
): BudgetRates {
  const overheadRate =
    typeof settings.defaultOverhead === "number"
      ? clampRateWithWarning(
          toDecimalRate(settings.defaultOverhead, "settings.defaultOverhead"),
          BUSINESS_RULES.MAX_SANE_OVERHEAD_RATE,
          "orgSettings.overheadRate",
        )
      : BUSINESS_RULES.DEFAULT_OVERHEAD_RATE;

  // Pass margin directly — engine's profitMargin path handles the math
  // Check field names: canonical "defaultMargin", legacy "defaultMarginPercentage",
  // and legacy "defaultMarkup" (written by pre-2026-03 onboarding — 41 orgs still have this key)
  const settingsRecord = settings as Record<string, unknown>;
  const rawMargin =
    typeof settings.defaultMargin === "number"
      ? settings.defaultMargin
      : typeof settingsRecord.defaultMarginPercentage === "number"
        ? (settingsRecord.defaultMarginPercentage as number)
        : typeof settingsRecord.defaultMarkup === "number"
          ? (settingsRecord.defaultMarkup as number)
          : undefined;
  const profitMargin =
    rawMargin !== undefined
      ? clampRateWithWarning(
          toDecimalRate(rawMargin, "settings.defaultMargin"),
          BUSINESS_RULES.MAX_SANE_PROFIT_MARGIN,
          "orgSettings.profitMargin",
        )
      : BUSINESS_RULES.DEFAULT_MARGIN_RATE;

  const salesTaxRate =
    typeof settings.defaultTaxRate === "number"
      ? settings.defaultTaxRate
      : undefined;

  const laborBurdenRate =
    typeof settings.laborBurdenRate === "number"
      ? toDecimalRate(settings.laborBurdenRate)
      : BUSINESS_RULES.DEFAULT_LABOR_BURDEN_RATE;

  const contingencyRate =
    typeof settings.defaultContingency === "number"
      ? toDecimalRate(settings.defaultContingency)
      : BUSINESS_RULES.DEFAULT_CONTINGENCY_RATE;

  // Read MORS fields from org settings (auto-derived during onboarding or user-configured)
  const overheadRecoveryMethod =
    (settings.overheadRecoveryMethod as BudgetRates["overheadRecoveryMethod"]) ??
    "percentage_revenue";

  const clamp = (val: number): number => Math.min(Math.max(val, 0), 0.99);
  const categoryOverheadRates =
    overheadRecoveryMethod === "activity_based" &&
    settings.categoryOverheadRates
      ? {
          labor: settings.categoryOverheadRates.labor
            ? clamp(toDecimalRate(settings.categoryOverheadRates.labor))
            : overheadRate,
          materials: settings.categoryOverheadRates.materials
            ? clamp(toDecimalRate(settings.categoryOverheadRates.materials))
            : overheadRate,
          equipment: settings.categoryOverheadRates.equipment
            ? clamp(toDecimalRate(settings.categoryOverheadRates.equipment))
            : overheadRate,
          subcontractors: settings.categoryOverheadRates.subcontractors
            ? clamp(
                toDecimalRate(settings.categoryOverheadRates.subcontractors),
              )
            : overheadRate,
        }
      : undefined;

  // Derive cost distribution from categoryMargins (revenue distribution).
  // categoryMargins are % of revenue per category; costDistribution needs
  // fractions of direct costs. Convert: category% / sum(all category%) → decimal.
  let costDistribution: BudgetRates["costDistribution"] = undefined;
  if (settings.categoryMargins) {
    const cm = settings.categoryMargins;
    const mat = cm.materials ?? 0;
    const lab = cm.labor ?? 0;
    const eq = cm.equipment ?? 0;
    const sub = cm.subcontractor ?? 0;
    const cogsTotal = mat + lab + eq + sub;
    if (cogsTotal > 0) {
      costDistribution = {
        materials: mat / cogsTotal,
        labor: lab / cogsTotal,
        equipment: eq / cogsTotal,
        subcontractors: sub / cogsTotal,
      };
    }
  }

  return {
    overheadRate,
    overheadBasis:
      overheadRecoveryMethod === "percentage_revenue"
        ? ("revenue" as const)
        : ("direct" as const),
    overheadRecoveryMethod,
    categoryOverheadRates,
    marginRate: 0, // Not used when profitMargin is set
    profitMargin,
    contingencyRate,
    includeContingency: true,
    salesTaxRate,
    laborBurdenRate,
    costDistribution,
  };
}
